import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_courseComponentMutation,
  useGET_courseComponentByIdQuery,
  usePOST_courseComponentMutation,
  usePUT_courseComponentMutation,
} from 'redux/api/coursesApi'
import { useGET_courseSelectQuery } from 'redux/api/coursesApi'
import { useGET_rubricSelectQuery } from 'redux/api/rubricApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'
import InputField from 'components/atoms/fields/InputField'
import CheckboxField from 'components/atoms/fields/CheckboxField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/courseComponentModel'
import { crudColorMap, crudIconMap, scoreTypeSelectData } from 'utils/helpers'

import { ActionType, ICourseComponentData } from 'types'
import DialogPopup from 'components/molecules/DialogPopup'
import Rubric from 'components/molecules/Rubric'


interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const CourseComponentForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = '/course-components'
  const editSlug = `/course-components/edit/${id}`

  const [rubricOpen, setRubricOpen] = useState(false)

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.course-components')}`,
    entityId: id,
    getEntityQuery: useGET_courseComponentByIdQuery,
    createEntityMutation: usePOST_courseComponentMutation,
    editEntityMutation: usePUT_courseComponentMutation,
    deleteEntityMutation: useDELETE_courseComponentMutation,
  })

  const { 
    title, 
    course, 
    score_type, 
    importance, 
    rubric,
    extra_credit,
    release_scores,
    final_grade,
    ungraded
  } = formModel()

  return (
    <Formik
      enableReinitialize
      initialValues={entity || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: ICourseComponentData = {
          course_id: values.course.id,
          score_type: values.score_type.id,
          title: values.title,
          importance: values.importance,
          is_modifiable: true,
          is_disabled: false,
          has_extra_credit: false,
          counts_to_final_grade: true,  
          are_scores_shown_to_students: true
        }
        handleSubmit(action, id, data)
      }}
    >{({ values, setFieldValue, errors }) => (
      <Form>
        <Grid container spacing={2} sx={{ marginTop: '-1rem' }}>
          <Grid item xs={12} lg={4}>
            <SelectField
              renderChildren={false}
              fieldProps={{ ...course }}
              apiCall={useGET_courseSelectQuery}
              disabled={viewOnly}
            />
          </Grid> 
          <Grid item xs={12} lg={4}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} lg={2}>
            <SelectField
                  fieldProps={{ ...score_type }}
                  apiCall={scoreTypeSelectData}
                  disabled={viewOnly}
                />
          </Grid>       
          <Grid item xs={12} lg={2}>
            <InputField fieldProps={{ ...importance }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} lg={4}>
              <div style={{ display: 'flex', width: '100%' }}>
                <div style={{ width: `calc(100% - 100px)` }}>
                  <SelectField
                    fieldProps={{ ...rubric }}
                    apiCall={useGET_rubricSelectQuery}
                    disabled={false}
                  />
                </div>
                {values?.rubric && <>
                  <Button
                    variant='outlined'
                    color='black'
                    className='small-button'
                    onClick={() => {
                      setRubricOpen(true)
                    }}
                    sx={{ marginLeft: '5px', width: '95px' }}
                  >
                    {t('common.preview')}
                  </Button>
                </>}
              </div>
          </Grid>        
          <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
            <CheckboxField fieldProps={{ ...extra_credit }} />
          </Grid>    
          <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
            <CheckboxField fieldProps={{ ...release_scores }} />
          </Grid>    
          <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
            <CheckboxField fieldProps={{ ...final_grade }} />
          </Grid>    
          <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
            <CheckboxField fieldProps={{ ...ungraded }} />
          </Grid>    


          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>




        </Grid>


        {values?.rubric?.id &&
          <DialogPopup
            title={values?.rubric?.title}
            open={rubricOpen}
            onClose={() => setRubricOpen(false)}
            popupWidth={'1000px'}
          >
           <Rubric id={values.rubric.id} />   
          </DialogPopup>
          }


      </Form>)}

    </Formik>
  )
}
export default CourseComponentForm
